import Link from 'next/link';
import Image from 'next/image';
import MaterialIcons from '../MaterialIcons/materialIcons';
import { CARD_FALLBACK_IMAGE } from '@/lib/utils/thumbnail-url';

interface BasicCardProps {
  url?: string;
  thumbnail?: {
    filename?: string;
    url?: string;
  };
  title: string;
  contentType: string;
  duration: string;
  showProgress?: boolean;
  progressPercent?: number;
  questions?: string;
  level?: string;
  releaseDate?: string;
  onClick?: () => void;
  /** 'homePage' = card-hover-zoom style (like CardsSection), 'default' = arrow/overlay style, 'none' = no hover */
  hoverVariant?: 'homePage' | 'default' | 'none';
}

export default function BasicCard({
  url,
  thumbnail,
  title,
  contentType,
  duration,
  showProgress = false,
  progressPercent = 0,
  questions,
  level,
  releaseDate,
  onClick,
  hoverVariant = 'default',
}: BasicCardProps) {
  const useHomePageHover = hoverVariant === 'homePage';
  const noHover = hoverVariant === 'none';

  const linkClass =
    noHover ? 'flex h-full w-full cursor-pointer flex-col bg-surface-2' :
    useHomePageHover ? 'flex flex-col w-full h-full card-hover-zoom group cursor-pointer' :
    'group bg-surface-3 flex h-full w-full cursor-pointer flex-col';

  const imageContainerClass =
    noHover ? 'relative aspect-[40/21] w-full flex-shrink-0 overflow-hidden rounded-lg bg-surface-2' :
    useHomePageHover ? 'relative aspect-[40/21] w-full flex-shrink-0 overflow-hidden rounded-lg bg-surface-2' :
    'bg-surface-3 relative aspect-[40/21] w-full flex-shrink-0 overflow-hidden rounded-lg';

  const imageClass =
    noHover ? 'desktop:aspect-[416/218] w-full object-cover' :
    useHomePageHover ? 'desktop:aspect-[416/218] w-full object-cover card-zoom-image' :
    'desktop:aspect-[416/218] w-full object-cover transition-transform duration-300 group-hover:scale-105';

  const contentClass =
    noHover ? 'flex w-full flex-grow flex-col p-6 bg-surface-2' :
    useHomePageHover ? 'flex w-full flex-grow flex-col p-6 bg-surface-2 card-content-hover transition-colors duration-300' :
    'bg-surface-3 flex w-full flex-grow flex-col p-6';

  const showOverlayAndArrow = !noHover && !useHomePageHover;

  return (
    <Link
      href={`${url}` || ''}
      target="_blank"
      className={linkClass}
    >
      <div className={imageContainerClass}>
        <Image
          loading="lazy"
          src={
            thumbnail?.url && thumbnail.url.trim() !== ''
              ? `${thumbnail.url}?width=416`
              : CARD_FALLBACK_IMAGE
          }
          height={218}
          width={416}
          className={imageClass}
          alt={thumbnail?.filename || title || 'Course thumbnail'}
        />
        {showOverlayAndArrow && (
          <>
            <div
              className="card-overlay absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              aria-hidden
            />
            <div className="absolute right-3 bottom-3 translate-y-2 transform opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
              <div className="bg-surface-3 text-text-strong hover:bg-surface-2 flex h-8 w-8 cursor-pointer items-center justify-center text-sm font-medium transition-colors duration-200">
                <MaterialIcons iconName="arrow_forward" className="text-primary-crystal" />
              </div>
            </div>
          </>
        )}
      </div>
      <div className={contentClass}>
        <h4 className="cs-h4 !mb-[4px] flex-none self-stretch line-clamp-1 sm:line-clamp-none">{title}</h4>
        <div className="content-details flex flex-none flex-row items-center gap-2">
          {/* Content Type Label */}
          {contentType && (
            <div className="content-type-label flex flex-none items-center justify-center">
              <span className="cs-caption">{contentType}</span>
            </div>
          )}

          {contentType && duration && <div className="divider" />}

          {/* Duration */}
          {duration && (
            <div className="duration flex flex-none items-center self-stretch">
              <span className="cs-caption">{duration}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
